import { pdf } from '@react-pdf/renderer'
import { SurveyDocument } from '../pdf/templates/SurveyDocument'

/**
 * Build categoryData object from separate aiDescriptions, ratings, and limitations
 */
function buildCategoryData(aiDescriptions = {}, categoryRatings = {}, limitations = {}) {
  const categoryData = {}

  // Add descriptions and ratings for each category
  for (const code of Object.keys(aiDescriptions)) {
    categoryData[code] = {
      comment: aiDescriptions[code] || '',
      rating: categoryRatings[code] || null
    }
  }

  // Add any ratings that weren't in descriptions
  for (const code of Object.keys(categoryRatings)) {
    if (!categoryData[code]) {
      categoryData[code] = { comment: '', rating: categoryRatings[code] }
    } else {
      categoryData[code].rating = categoryRatings[code]
    }
  }

  // Add limitations
  for (const [key, text] of Object.entries(limitations)) {
    categoryData[key] = { comment: text, rating: null }
  }

  return categoryData
}

/**
 * Generate PDF document using @react-pdf/renderer
 *
 * @param {Object} survey - Survey object with property details
 * @param {Array} photos - Array of photo objects with { id, categoryCode, imageUrl }
 * @param {Object} aiDescriptions - Object mapping category codes to description text
 * @param {Object} categoryRatings - Object mapping category codes to ratings (1, 2, 3, or NI)
 * @param {Object} limitations - Object mapping section limitations keys to text
 * @param {Array} customCategories - Array of custom category objects
 * @returns {Promise<Blob>} PDF document as blob
 */
export async function generateDocumentReactPdf(
  survey,
  photos,
  aiDescriptions = {},
  categoryRatings = {},
  limitations = {},
  customCategories = []
) {
  // Build unified categoryData object
  const categoryData = buildCategoryData(aiDescriptions, categoryRatings, limitations)

  console.log('Generating PDF with react-pdf:', {
    survey: survey?.propertyName,
    photos: photos?.length,
    categories: Object.keys(categoryData).length,
    customCategories: customCategories?.length
  })

  // Generate PDF blob
  const blob = await pdf(
    <SurveyDocument
      survey={survey}
      photos={photos}
      categoryData={categoryData}
      customCategories={customCategories}
    />
  ).toBlob()

  return blob
}

/**
 * Trigger download of a blob as a file
 */
export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export default {
  generateDocumentReactPdf,
  triggerDownload
}
